import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiErrorMessage, apiGet } from "../../api/client";
import { roleLabel } from "../../lib/roleLabel";
import { formatWhen } from "../../lib/when";

/**
 * Threads (SRS §5.4): what a person asked and what they were told, read-only,
 * for the Supervisor and the reviewer who answer for it. Decisions are
 * marked; the thread can be narrowed to one agent and a span of days, and
 * exported as a CSV exactly as shown.
 */
interface Person {
  user_id: string;
  name: string;
  role: string;
  messages: number;
  last_at: string | null;
}
interface Message {
  id: string;
  kind: string;
  body_en: string;
  body_ar: string;
  ref_run_id: string | null;
  hand_key: string | null;
  created_at: string;
  is_fork: boolean;
}

const AGENTS: [string, string][] = [
  ["h01_sales_analyst", "Sales Performance Analyst"],
  ["h02_weekly_pack", "Weekly pack"],
  ["h05_board_pack", "Board pack"],
  ["h03_kyc_gate", "KYC case readiness"],
  ["h06_marketing_gate", "Marketing compliance"],
  ["h07_digital_performance", "Digital performance"],
  ["h10_vendor_governance", "Vendor governance"],
  ["a03_audit_report", "Audit report"],
];
const WHO: Record<string, string> = { user: "Asked", assistant: "Answer", result: "Result", system: "Note", fork: "Decision" };

/** A cell a spreadsheet will not run: quotes doubled, and a leading = + - @ made text. */
const cell = (v: string) => `"${(/^[=+\-@\t\r]/.test(v) ? `'${v}` : v).replace(/"/g, '""')}"`;

export function Threads() {
  const { workspaceId = "" } = useParams();
  const [user, setUser] = useState("");
  const [hand, setHand] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const params = new URLSearchParams();
  if (user) params.set("user", user);
  if (hand) params.set("hand", hand);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const qs = params.toString();
  const q = useQuery({
    queryKey: ["console-threads", workspaceId, qs],
    queryFn: () => apiGet<{ people: Person[]; messages: Message[] }>(`/api/workspaces/${workspaceId}/threads${qs ? `?${qs}` : ""}`),
    // Keep the thread on screen while a filter change loads, so it does not blink empty.
    placeholderData: (prev) => prev,
  });
  const people = q.data?.people ?? [];
  const messages = user ? (q.data?.messages ?? []) : [];
  const person = people.find((p) => p.user_id === user);
  const csv = useMemo(() => {
    const rows = [["when", "kind", "agent", "run", "text"].join(",")];
    for (const m of messages) rows.push([m.created_at, WHO[m.kind] ?? m.kind, m.hand_key ?? "", m.ref_run_id ?? "", m.body_en].map(cell).join(","));
    return `data:text/csv;charset=utf-8,${encodeURIComponent(rows.join("\n"))}`;
  }, [messages]);

  return (
    <div className="main-inner" data-testid="threads">
      <div className="greet-row">
        <span className="greet">Threads</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">WHAT WAS ASKED AND DECIDED</div>
        <div className="lede">Read-only. Pick a person to read their thread.</div>
      </div>

      <div className="threads-filters">
        <label>
          Person
          <select data-testid="thread-person" value={user} onChange={(e) => setUser(e.target.value)}>
            <option value="">Choose…</option>
            {people.map((p) => (
              <option key={p.user_id} value={p.user_id}>
                {p.name} · {roleLabel(p.role)} · {p.messages} messages
              </option>
            ))}
          </select>
        </label>
        <label>
          Agent
          <select data-testid="thread-hand" value={hand} onChange={(e) => setHand(e.target.value)}>
            <option value="">All agents</option>
            {AGENTS.map(([k, n]) => (
              <option key={k} value={k}>{n}</option>
            ))}
          </select>
        </label>
        <label>
          From
          <input type="date" data-testid="thread-from" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          To
          <input type="date" data-testid="thread-to" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        {user && messages.length > 0 ? (
          <a className="btn" data-testid="thread-csv" href={csv} download={`thread-${(person?.name ?? "person").replace(/\s+/g, "-").toLowerCase()}.csv`}>
            Export CSV
          </a>
        ) : null}
      </div>

      {q.isError ? <span className="pill red">{apiErrorMessage(q.error)}</span> : null}
      {user && !q.isLoading && messages.length === 0 ? <div className="play-meta">Nothing in this thread for these filters.</div> : null}
      <ol className="threads-log">
        {messages.map((m) => (
          <li key={m.id} data-testid={`thread-msg-${m.id}`} className={`threads-msg kind-${m.kind}${m.is_fork ? " is-fork" : ""}`}>
            <div className="threads-meta">
              <span className={`pill ${m.is_fork ? "amber" : ""}`}>{WHO[m.kind] ?? m.kind}</span>
              <span>{formatWhen(m.created_at)}</span>
              {m.ref_run_id ? (
                <a href={`/workspaces/${workspaceId}/workforce/runs/${m.ref_run_id}`}>How this was made</a>
              ) : null}
            </div>
            <div className="threads-body">{m.body_en}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}
